(() => {
  const ROOT_SELECTOR = 'mg-v36-bundle-builder';

  const normalise = (value) => String(value || '').trim().toLowerCase();

  class MGV36BundleBuilder extends HTMLElement {
    constructor() {
      super();
      this.components = [];
      this.submitting = false;
      this.attemptedSubmit = false;
      this.afterAdd = 'cart';
      this.currency = 'AUD';
      this.bundleTitle = '';
      this.bundleVariantId = 0;
      this.bundlePrice = 0;
      this.bundleRrp = 0;
      this.bundleAvailable = false;
      this.ctaLabel = 'Add complete bundle to cart';
      this.workingLabel = 'Adding your bundle...';
      this.successLabel = 'Your personalised bundle has been added.';
      this.errorLabel = 'Please complete every required bundle selection.';
      this.lastDialogTrigger = null;
      this.stickyGalleryFrame = 0;
      this.stickyGalleryResizeObserver = null;
      this.handleStickyGalleryViewport = null;
    }

    connectedCallback() {
      if (this.dataset.initialised === 'true') return;
      this.dataset.initialised = 'true';

      this.currency = this.dataset.currency || 'AUD';
      this.bundleTitle = this.dataset.bundleTitle || 'Mr Golf bundle';
      this.bundleVariantId = Number(this.dataset.bundleVariantId || 0);
      this.bundlePrice = Math.max(0, Number(this.dataset.bundlePrice || 0));
      this.bundleRrp = Math.max(this.bundlePrice, Number(this.dataset.bundleRrp || 0));
      this.bundleAvailable = this.dataset.bundleAvailable === 'true';
      this.afterAdd = this.dataset.afterAdd || 'cart';
      this.cartUrl = this.dataset.cartUrl || '/cart';
      this.ctaLabel = this.dataset.ctaLabel || this.ctaLabel;
      this.workingLabel = this.dataset.workingLabel || this.workingLabel;
      this.successLabel = this.dataset.successLabel || this.successLabel;
      this.errorLabel = this.dataset.errorLabel || this.errorLabel;
      this.statusElement = this.querySelector('[data-bundle-status]');
      this.addButton = this.querySelector('[data-add-bundle]');
      this.mobileAddButton = this.querySelector('[data-mobile-add]');

      this.components = Array.from(this.querySelectorAll('[data-bundle-component]'))
        .map((element) => this.createComponentState(element))
        .filter(Boolean);

      this.bindEvents();
      this.components.forEach((component) => this.refreshComponent(component));
      this.update();
      this.setupStickyGallery();
    }

    disconnectedCallback() {
      if (this.handleStickyGalleryViewport) {
        window.removeEventListener('resize', this.handleStickyGalleryViewport);
        window.removeEventListener('scroll', this.handleStickyGalleryViewport);
      }
      if (this.stickyGalleryFrame) window.cancelAnimationFrame(this.stickyGalleryFrame);
      this.stickyGalleryResizeObserver?.disconnect();
    }

    setupStickyGallery() {
      const gallery = this.querySelector('[data-bundle-gallery]');
      if (!gallery) return;

      const scheduleUpdate = () => {
        if (this.stickyGalleryFrame) return;
        this.stickyGalleryFrame = window.requestAnimationFrame(() => {
          this.stickyGalleryFrame = 0;
          this.updateStickyGalleryMetrics();
        });
      };

      this.handleStickyGalleryViewport = scheduleUpdate;
      window.addEventListener('resize', scheduleUpdate, { passive: true });
      window.addEventListener('scroll', scheduleUpdate, { passive: true });

      if ('ResizeObserver' in window) {
        this.stickyGalleryResizeObserver = new ResizeObserver(scheduleUpdate);
        this.stickyGalleryResizeObserver.observe(gallery);
      }

      scheduleUpdate();
      window.setTimeout(scheduleUpdate, 250);
      window.setTimeout(scheduleUpdate, 900);
    }

    visibleStickyHeaderBottom() {
      const candidates = new Set();
      const probeX = Math.max(1, Math.min(window.innerWidth / 2, window.innerWidth - 1));

      if (typeof document.elementsFromPoint === 'function') {
        [1, 8].forEach((probeY) => {
          document.elementsFromPoint(probeX, probeY).forEach((element) => {
            let current = element;
            while (current && current !== document.documentElement) {
              candidates.add(current);
              current = current.parentElement;
            }
          });
        });
      }

      document.querySelectorAll([
        '.shopify-section-group-header-group',
        '#shopify-section-header',
        '.shopify-section-header',
        'sticky-header',
        'header[role="banner"]',
        '.header-wrapper'
      ].join(',')).forEach((element) => candidates.add(element));

      let headerBottom = 0;
      candidates.forEach((element) => {
        if (!(element instanceof Element) || this.contains(element)) return;
        const style = window.getComputedStyle(element);
        if (style.position !== 'fixed' && style.position !== 'sticky') return;

        const rect = element.getBoundingClientRect();
        const topHeaderLimit = Math.min(240, window.innerHeight * 0.3);
        const isAtViewportTop = rect.top <= topHeaderLimit && rect.bottom > 0;
        const isPlausibleHeader = rect.height > 0 && rect.bottom < window.innerHeight * 0.55;
        if (isAtViewportTop && isPlausibleHeader) {
          headerBottom = Math.max(headerBottom, rect.bottom);
        }
      });

      return Math.max(0, Math.ceil(headerBottom));
    }

    updateStickyGalleryMetrics() {
      if (!window.matchMedia('(min-width: 921px)').matches) {
        this.dataset.gallerySticky = 'false';
        this.style.removeProperty('--mg-gallery-sticky-top');
        return;
      }

      const headerBottom = this.visibleStickyHeaderBottom();
      const stickyTop = Math.max(24, headerBottom + 16);
      const gallery = this.querySelector('[data-bundle-gallery]');
      const galleryHeight = gallery ? Math.ceil(gallery.getBoundingClientRect().height) : 0;
      const availableGalleryHeight = Math.floor(window.innerHeight - stickyTop - 24);

      if (!galleryHeight || galleryHeight > availableGalleryHeight) {
        this.dataset.gallerySticky = 'false';
        this.style.removeProperty('--mg-gallery-sticky-top');
        return;
      }

      this.dataset.gallerySticky = 'true';
      this.style.setProperty('--mg-gallery-sticky-top', `${stickyTop}px`);
    }

    createComponentState(element) {
      const products = Array.from(element.querySelectorAll('[data-component-product-panel]'))
        .map((panel) => this.createProductState(panel))
        .filter(Boolean);

      if (!products.length) return null;

      const selectedProductId = element.querySelector('[data-component-product-choice].is-selected')?.dataset.productId
        || element.querySelector('[data-component-product-panel]:not([hidden])')?.dataset.productId;
      const currentProduct = products.find((product) => String(product.id) === String(selectedProductId))
        || products.find((product) => product.variant?.available)
        || products[0];
      const foilSelected = element.querySelector('[data-foil-button].is-selected')
        || element.querySelector('[data-foil-button]');

      if (foilSelected) {
        foilSelected.classList.add('is-selected');
        foilSelected.setAttribute('aria-pressed', 'true');
      }

      return {
        element,
        products,
        currentProduct,
        required: element.dataset.required === 'true',
        active: element.dataset.active === 'true',
        quantity: Math.max(1, Number(element.dataset.quantity || 1)),
        title: element.dataset.componentTitle || currentProduct.title || 'Bundle product',
        personalisationEnabled: element.dataset.personalisationEnabled === 'true',
        personalisationRequired: element.dataset.personalisationRequired === 'true',
        foilEnabled: element.dataset.foilEnabled === 'true',
        foilRequired: element.dataset.foilRequired === 'true',
        personalisationInput: element.querySelector('[data-personalisation-input]'),
        selectedFoil: foilSelected?.dataset.foilValue || ''
      };
    }

    createProductState(panel) {
      const variantsNode = panel.querySelector('[data-component-variants]');
      let variants = [];

      try {
        variants = variantsNode ? JSON.parse(variantsNode.textContent || '[]') : [];
      } catch (error) {
        console.error('[Mr Golf bundle] Could not read component variants.', error);
      }

      if (!variants.length) return null;

      const optionGroups = Array.from(panel.querySelectorAll('[data-option-group]'))
        .sort((a, b) => Number(a.dataset.optionIndex) - Number(b.dataset.optionIndex));
      const optionNames = optionGroups.map((group, index) => (
        String(group.dataset.optionName || `Option ${index + 1}`).trim()
      ));
      const selectedOptions = optionGroups.map((group) => {
        const selected = group.querySelector('[data-option-button].is-selected');
        const first = group.querySelector('[data-option-button]');
        return String(selected?.dataset.optionValue || first?.dataset.optionValue || '');
      });

      let variant = this.findVariant(variants, selectedOptions);
      if (!variant || !variant.available) {
        variant = variants.find((candidate) => candidate.available) || variants[0];
      }

      return {
        id: panel.dataset.productId || '',
        title: panel.dataset.productTitle || 'Bundle product',
        panel,
        variants,
        optionGroups,
        optionNames,
        selectedOptions: variant?.options?.length ? variant.options.slice() : selectedOptions,
        variant
      };
    }

    bindEvents() {
      this.addEventListener('click', (event) => {
        const closeGuideButton = event.target.closest('[data-close-guide-dialog]');
        if (closeGuideButton && this.contains(closeGuideButton)) {
          this.closeGuideDialog(closeGuideButton.closest('[data-bundle-guide-dialog]'));
          return;
        }

        const guideTrigger = event.target.closest('[data-open-hand-guide], [data-open-size-guide]');
        if (guideTrigger && this.contains(guideTrigger)) {
          const panel = guideTrigger.closest('[data-component-product-panel]');
          const selector = guideTrigger.matches('[data-open-hand-guide]')
            ? '[data-hand-guide-dialog]'
            : '[data-size-guide-dialog]';
          this.openGuideDialog(panel?.querySelector(selector), guideTrigger);
          return;
        }

        const guideBackdrop = event.target.closest('[data-bundle-guide-dialog]');
        if (guideBackdrop && event.target === guideBackdrop) {
          this.closeGuideDialog(guideBackdrop);
          return;
        }

        const galleryThumb = event.target.closest('[data-gallery-thumb]');
        if (galleryThumb && this.contains(galleryThumb)) {
          this.activateGalleryImage(galleryThumb);
          return;
        }

        const toggle = event.target.closest('[data-component-toggle]');
        if (toggle && this.contains(toggle)) {
          const component = this.componentForElement(toggle);
          if (component && !component.required) {
            component.active = !component.active;
            component.element.dataset.active = String(component.active);
            this.refreshComponent(component);
            this.update();
          }
          return;
        }

        const productChoice = event.target.closest('[data-component-product-choice]');
        if (productChoice && this.contains(productChoice) && !productChoice.disabled) {
          const component = this.componentForElement(productChoice);
          if (component) {
            this.selectComponentProduct(component, productChoice.dataset.productId || '');
            this.refreshComponent(component);
            this.update();
          }
          return;
        }

        const optionButton = event.target.closest('[data-option-button]');
        if (optionButton && this.contains(optionButton) && !optionButton.disabled) {
          const component = this.componentForElement(optionButton);
          if (component) {
            this.selectOption(
              component,
              Number(optionButton.dataset.optionIndex || 0),
              optionButton.dataset.optionValue || ''
            );
            this.refreshComponent(component);
            this.update();
          }
          return;
        }

        const foilButton = event.target.closest('[data-foil-button]');
        if (foilButton && this.contains(foilButton)) {
          const component = this.componentForElement(foilButton);
          if (component) {
            component.selectedFoil = foilButton.dataset.foilValue || '';
            component.element.querySelectorAll('[data-foil-button]').forEach((button) => {
              const selected = button === foilButton;
              button.classList.toggle('is-selected', selected);
              button.setAttribute('aria-pressed', String(selected));
            });
            this.update();
          }
          return;
        }

        const addButton = event.target.closest('[data-add-bundle], [data-mobile-add]');
        if (addButton && this.contains(addButton)) {
          this.addBundle();
        }
      });

      this.addEventListener('input', (event) => {
        if (!event.target.matches('[data-personalisation-input]')) return;
        const component = this.componentForElement(event.target);
        if (!component) return;

        event.target.value = String(event.target.value || '').toUpperCase();
        const counter = component.element.querySelector('[data-character-count]');
        if (counter) counter.textContent = String(event.target.value.length);
        this.update();
      });
    }

    componentForElement(element) {
      const componentElement = element.closest('[data-bundle-component]');
      return this.components.find((component) => component.element === componentElement) || null;
    }

    openGuideDialog(dialog, trigger) {
      if (!dialog || dialog.open) return;

      this.lastDialogTrigger = trigger || null;
      dialog.addEventListener('close', () => {
        this.lastDialogTrigger?.focus?.({ preventScroll: true });
      }, { once: true });

      try {
        if (typeof dialog.showModal === 'function') dialog.showModal();
        else dialog.setAttribute('open', '');
      } catch (error) {
        console.warn('[Mr Golf bundle] Native dialog failed; using open attribute.', error);
        dialog.setAttribute('open', '');
      }
    }

    closeGuideDialog(dialog) {
      if (!dialog) return;

      if (typeof dialog.close === 'function') {
        dialog.close();
      } else {
        dialog.removeAttribute('open');
        this.lastDialogTrigger?.focus?.({ preventScroll: true });
      }
    }

    findVariant(variants, selectedOptions) {
      return variants.find((variant) => (
        Array.isArray(variant.options)
        && variant.options.length === selectedOptions.length
        && variant.options.every((value, index) => String(value) === String(selectedOptions[index]))
      ));
    }

    selectComponentProduct(component, productId) {
      const nextProduct = component.products.find((product) => String(product.id) === String(productId));
      if (!nextProduct) return;
      component.currentProduct = nextProduct;
    }

    selectOption(component, optionIndex, optionValue) {
      const product = component.currentProduct;
      product.selectedOptions[optionIndex] = optionValue;

      let nextVariant = this.findVariant(product.variants, product.selectedOptions);
      if (!nextVariant || !nextVariant.available) {
        nextVariant = product.variants.find((variant) => {
          if (!variant.available) return false;
          return variant.options.every((value, index) => {
            if (index > optionIndex) return true;
            return String(value) === String(product.selectedOptions[index]);
          });
        });
      }

      if (nextVariant) {
        product.variant = nextVariant;
        product.selectedOptions = nextVariant.options.slice();
      } else {
        product.variant = this.findVariant(product.variants, product.selectedOptions) || product.variant;
      }
    }

    refreshComponent(component) {
      const { element } = component;
      const product = component.currentProduct;
      element.classList.toggle('is-inactive', !component.active);

      const body = element.querySelector('[data-component-body]');
      if (body) body.hidden = !component.active;

      const toggle = element.querySelector('[data-component-toggle]');
      if (toggle) {
        toggle.classList.toggle('is-selected', component.active);
        toggle.setAttribute('aria-pressed', String(component.active));
        const label = toggle.querySelector('[data-toggle-label]');
        if (label) label.textContent = component.active ? 'Added' : 'Add';
      }

      element.querySelectorAll('[data-component-product-choice]').forEach((button) => {
        const selected = String(button.dataset.productId) === String(product.id);
        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-pressed', String(selected));
      });

      component.products.forEach((candidate) => {
        candidate.panel.hidden = candidate !== product;
      });

      product.optionGroups.forEach((group, optionIndex) => {
        group.querySelectorAll('[data-option-button]').forEach((button) => {
          const value = button.dataset.optionValue || '';
          const selected = String(product.selectedOptions[optionIndex]) === String(value);
          const possible = product.variants.some((variant) => {
            if (!variant.available || String(variant.options[optionIndex]) !== String(value)) return false;
            return variant.options.every((variantValue, index) => {
              if (index >= optionIndex) return true;
              return String(variantValue) === String(product.selectedOptions[index]);
            });
          });

          button.classList.toggle('is-selected', selected);
          button.setAttribute('aria-pressed', String(selected));
          button.disabled = !possible;
          button.classList.toggle('is-unavailable', !possible);

          const colourChip = button.querySelector('[data-colour-chip]');
          if (colourChip) {
            colourChip.style.backgroundColor = this.colourForName(colourChip.dataset.colourName || value);
          }
        });
      });

      product.variant = this.findVariant(product.variants, product.selectedOptions)
        || product.variant
        || product.variants.find((variant) => variant.available)
        || product.variants[0];

      const image = element.querySelector('[data-component-image]');
      if (image && product.variant?.image) {
        image.src = product.variant.image;
        image.alt = product.title;
      }

      if (component.personalisationInput) {
        const counter = element.querySelector('[data-character-count]');
        if (counter) counter.textContent = String(component.personalisationInput.value.length);
      }
    }

    colourForName(value) {
      const name = normalise(value);
      const colours = [
        ['navy', '#191c3b'],
        ['black', '#111111'],
        ['white', '#f7f7f5'],
        ['ivory', '#eee8dc'],
        ['cream', '#eee8dc'],
        ['tan', '#b9956e'],
        ['brown', '#694b37'],
        ['chocolate', '#4f3529'],
        ['camel', '#b88a59'],
        ['grey', '#8a8d94'],
        ['gray', '#8a8d94'],
        ['silver', '#b9bcc4'],
        ['gold', '#b6813c'],
        ['green', '#286149'],
        ['olive', '#6f7442'],
        ['blue', '#315a86'],
        ['red', '#9c3434'],
        ['burgundy', '#6f2638'],
        ['pink', '#d597a8'],
        ['orange', '#c96e2e'],
        ['yellow', '#d2ad3f'],
        ['purple', '#6a4b78']
      ];

      return colours.find(([key]) => name.includes(key))?.[1] || '#d8d3ca';
    }

    activeComponents() {
      return this.components.filter((component) => component.active || component.required);
    }

    componentValidation(component) {
      if (!component.active && !component.required) {
        return { valid: true, variant: true, personalisation: true, foil: true };
      }

      const variantValid = Boolean(component.currentProduct?.variant?.available);
      const personalisationValid = !component.personalisationEnabled
        || !component.personalisationRequired
        || Boolean(String(component.personalisationInput?.value || '').trim());
      const foilValid = !component.foilEnabled
        || !component.foilRequired
        || Boolean(String(component.selectedFoil || '').trim());

      return {
        valid: variantValid && personalisationValid && foilValid,
        variant: variantValid,
        personalisation: personalisationValid,
        foil: foilValid
      };
    }

    applyValidationState(component) {
      const validation = this.componentValidation(component);
      const showErrors = this.attemptedSubmit && (component.active || component.required);
      component.element.classList.toggle('has-error', showErrors && !validation.valid);

      if (component.personalisationInput) {
        component.personalisationInput.setAttribute(
          'aria-invalid',
          String(showErrors && !validation.personalisation)
        );
      }

      const foilField = component.element.querySelector('.mg-bundle__foil-field');
      foilField?.classList.toggle('has-error', showErrors && !validation.foil);
      return validation.valid;
    }

    update() {
      const active = this.activeComponents();
      const valid = this.bundleAvailable
        && this.bundleVariantId > 0
        && active.length > 0
        && this.components.every((component) => this.applyValidationState(component));

      this.renderSummary(active);

      const introPrice = this.querySelector('[data-bundle-intro-price]');
      if (introPrice) introPrice.textContent = this.formatMoney(this.bundlePrice);

      const desktopPrice = this.querySelector('[data-add-price]');
      if (desktopPrice) desktopPrice.textContent = this.formatMoney(this.bundlePrice);

      const mobilePrice = this.querySelector('[data-mobile-price]');
      if (mobilePrice) mobilePrice.textContent = this.formatMoney(this.bundlePrice);

      const disabled = !valid || this.submitting;
      if (this.addButton) this.addButton.disabled = disabled;
      if (this.mobileAddButton) {
        this.mobileAddButton.disabled = disabled;
        this.mobileAddButton.textContent = this.submitting
          ? this.workingLabel
          : this.ctaLabel;
      }

      const label = this.querySelector('[data-add-label]');
      if (label) label.textContent = this.submitting ? this.workingLabel : this.ctaLabel;

      if (this.attemptedSubmit && !valid && this.statusElement) {
        this.statusElement.textContent = this.errorLabel;
      }
    }

    renderSummary(active) {
      const container = this.querySelector('[data-summary-items]');
      if (container) {
        container.replaceChildren();

        active.forEach((component) => {
          const row = document.createElement('div');
          row.className = 'mg-bundle__summary-item';

          const copy = document.createElement('div');
          const title = document.createElement('strong');
          const details = document.createElement('span');

          title.textContent = component.quantity > 1
            ? `${component.title} x ${component.quantity}`
            : component.title;

          const detailValues = [];
          if (component.products.length > 1) {
            detailValues.push(component.currentProduct.title);
          }
          if (component.currentProduct.variant?.title && component.currentProduct.variant.title !== 'Default Title') {
            detailValues.push(component.currentProduct.variant.title);
          }
          const personalisation = String(component.personalisationInput?.value || '').trim();
          if (personalisation) detailValues.push(personalisation);
          const foilName = this.foilDisplayName(component);
          if (foilName) detailValues.push(foilName);

          details.textContent = detailValues.join(' | ');

          copy.append(title, details);
          row.append(copy);
          container.append(row);
        });
      }

      const count = this.querySelector('[data-summary-count]');
      if (count) count.textContent = `${active.length} ${active.length === 1 ? 'product' : 'products'}`;

      const priceElement = this.querySelector('[data-summary-price]');
      if (priceElement) priceElement.textContent = this.formatMoney(this.bundlePrice);
    }

    formatMoney(cents) {
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en-AU', {
          style: 'currency',
          currency: this.currency
        }).format(Number(cents || 0) / 100);
      } catch (error) {
        return `$${(Number(cents || 0) / 100).toFixed(2)}`;
      }
    }

    activateGalleryImage(button) {
      const image = this.querySelector('[data-gallery-main]');
      if (!image) return;

      image.src = button.dataset.imageSrc || image.src;
      image.alt = button.dataset.imageAlt || this.bundleTitle;
      this.querySelectorAll('[data-gallery-thumb]').forEach((thumb) => {
        const selected = thumb === button;
        thumb.classList.toggle('is-selected', selected);
        thumb.setAttribute('aria-pressed', String(selected));
      });
    }

    firstInvalidElement() {
      for (const component of this.activeComponents()) {
        const validation = this.componentValidation(component);
        if (!validation.variant) return component.currentProduct.panel.querySelector('[data-option-group]');
        if (!validation.personalisation) return component.personalisationInput;
        if (!validation.foil) return component.element.querySelector('[data-foil-button]');
      }
      return null;
    }

    optionPropertyLabel(name, index) {
      const label = String(name || '').trim();
      if (!label) return `Option ${index + 1}`;
      const normalisedLabel = normalise(label);
      if (normalisedLabel.includes('hand')) return 'Hand';
      if (normalisedLabel.includes('size')) return 'Size';
      if (normalisedLabel.includes('colour') || normalisedLabel.includes('color')) return 'Colour';
      return label;
    }

    foilDisplayName(component) {
      if (!component?.foilEnabled) return '';

      const currentValue = String(component.selectedFoil || '').trim();
      const selectedButton = component.element.querySelector('[data-foil-button].is-selected');
      const buttonLabel = String(
        selectedButton?.dataset.tooltip
        || selectedButton?.getAttribute('aria-label')
        || ''
      ).trim();
      const isHex = (value) => /^#[0-9a-f]{3,8}$/i.test(value);

      if (buttonLabel && !isHex(buttonLabel)) return buttonLabel;
      if (currentValue && !isHex(currentValue)) return currentValue;

      const knownFoils = {
        '#b6813c': 'Gold Foil',
        '#b9bcc4': 'Silver Foil',
        '#191c3b': 'Black Foil',
        '#f5f3ee': 'White Foil',
        '#ffffff': 'White Foil'
      };

      return knownFoils[normalise(currentValue)]
        || knownFoils[normalise(buttonLabel)]
        || buttonLabel
        || currentValue;
    }

    bundleLineItemProperties(active, bundleId) {
      const properties = {};

      active.forEach((component, index) => {
        const itemNumber = index + 1;
        const product = component.currentProduct;
        const personalisation = String(component.personalisationInput?.value || '').trim().toUpperCase();
        const foilName = this.foilDisplayName(component);
        const details = [];

        product.selectedOptions.forEach((optionValue, optionIndex) => {
          if (!optionValue || optionValue === 'Default Title') return;
          const optionLabel = this.optionPropertyLabel(product.optionNames[optionIndex], optionIndex);
          details.push(`${optionLabel}: ${optionValue}`);
        });

        if (component.quantity > 1) details.push(`Quantity: ${component.quantity}`);
        if (component.personalisationEnabled && personalisation) {
          details.push(`Personalisation: ${personalisation}`);
        }
        if (foilName) {
          details.push(`Foil colour: ${foilName}`);
        }

        properties[`${itemNumber}. ${product.title}`] = details.join(' · ') || 'Included';
      });

      properties['_MG Bundle ID'] = bundleId;
      properties['_MG Bundle selection IDs'] = active
        .map((component) => `${component.currentProduct.variant.id}x${component.quantity}`)
        .join(',');

      return properties;
    }

    async addBundle() {
      if (this.submitting) return;
      this.attemptedSubmit = true;
      this.update();

      const invalid = this.firstInvalidElement();
      if (invalid) {
        invalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
        invalid.focus?.({ preventScroll: true });
        return;
      }

      const active = this.activeComponents();
      if (!active.length) return;

      const bundleId = `MG-${Date.now()}-${Math.random().toString(36).slice(2, 8).toUpperCase()}`;
      const items = [{
        id: this.bundleVariantId,
        quantity: 1,
        properties: this.bundleLineItemProperties(active, bundleId)
      }];

      this.submitting = true;
      if (this.statusElement) this.statusElement.textContent = this.workingLabel;
      this.update();

      try {
        const root = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({ items })
        });

        const result = await response.json();
        if (!response.ok) {
          throw new Error(result.description || result.message || 'The bundle could not be added.');
        }

        if (this.statusElement) this.statusElement.textContent = this.successLabel;
        document.dispatchEvent(new CustomEvent('cart:refresh', {
          bubbles: true,
          detail: { source: 'mg-v36-bundle-builder', result }
        }));

        if (this.afterAdd === 'stay') {
          this.submitting = false;
          this.update();
          return;
        }

        const currentPage = `${window.location.pathname}${window.location.search}`;
        const target = this.afterAdd === 'checkout'
          ? `${root}checkout`
          : this.afterAdd === 'stay'
            ? currentPage
            : this.cartUrl;
        window.location.assign(target);
      } catch (error) {
        console.error('[Mr Golf bundle] Add to cart failed.', error);
        this.submitting = false;
        if (this.statusElement) {
          this.statusElement.textContent = error.message || 'The bundle could not be added. Please try again.';
        }
        this.update();
      }
    }
  }

  if (!customElements.get(ROOT_SELECTOR)) {
    customElements.define(ROOT_SELECTOR, MGV36BundleBuilder);
  }
})();
